import React, { useState, useEffect } from 'react';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  RedditShareButton,
  PinterestShareButton,
  WhatsappShareButton,
  TelegramShareButton,
  EmailShareButton,
  TumblrShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  RedditIcon,
  PinterestIcon,
  WhatsappIcon,
  TelegramIcon,
  EmailIcon,
  TumblrIcon,
} from 'react-share';
import {
  Box,
  Text,
  Button,
  Heading,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Switch,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Flex,
  Stack,
  Divider,
  Tooltip,
  Collapse,
  IconButton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { MdGraphicEq } from 'react-icons/md';
import { track } from '@amplitude/analytics-browser';


export default function CookingCalculators() {
  const [weight, setWeight] = useState(5); // Default weight in lbs
  const [doneness, setDoneness] = useState('medium');
  const [unit, setUnit] = useState('kgs'); // 'lbs' or 'kgs'
  const [includeBones, setIncludeBones] = useState(false);
  const [numberOfBones, setNumberOfBones] = useState(2); // Default number of bones
  const [tempUnit, setTempUnit] = useState('C'); // 'F' or 'C'
  const [meatType, setMeatType] = useState('prime-rib'); // 'prime-rib', 'turkey', 'chicken', 'lamb', or 'pork'
  const shareUrl = 'https://cooktimecalc.com'; // The URL you want to share
  const [instructionsOpen, setInstructionsOpen] = useState(false); // Open by default
  const title = 'Check out this amazing cooking calculator!';

  // Effect to convert weight when unit changes
  useEffect(() => {
    if (unit === 'kgs') {
      setWeight(parseFloat((weight / 2.20462).toFixed(2))); // Convert lbs to kgs
    } else {
      setWeight(parseFloat((weight * 2.20462).toFixed(2))); // Convert kgs to lbs
    }
  }, [unit]);

   // Track Slider Change
   const handleSliderChange = (val) => {
    setWeight(val);
    track('Weight Slider Changed', {
      weight: val,
      unit,
    });
  };

// Track Doneness Button Click
const handleDonenessClick = (level) => {
  setDoneness(level);
  track('Doneness Button Clicked', {
    doneness: level,
    meatType,
  });
};


  // Track Meat Type Change
  const handleMeatTypeChange = (index) => {
    const types = ['prime-rib', 'turkey', 'chicken', 'lamb', 'pork'];
    const selectedMeat = types[index];
    setMeatType(selectedMeat);
    if (selectedMeat === 'pork') {
      setDoneness('medium');
    }
    track('Meat Type Changed', {
      meatType: selectedMeat,
    });
  };

  // Track Switch Change
  const handleUnitSwitchChange = () => {
    const newUnit = unit === 'lbs' ? 'kgs' : 'lbs';
    setUnit(newUnit);
    track('Unit Switch Changed', {
      unit: newUnit,
    });
  };

  const handleTempUnitSwitchChange = () => {
    const newTempUnit = tempUnit === 'F' ? 'C' : 'F';
    setTempUnit(newTempUnit);
    track('Temperature Unit Switch Changed', {
      tempUnit: newTempUnit,
    });
  };

  // Track Bone Inclusion Switch
  const handleBoneSwitchChange = () => {
    const newIncludeBones = !includeBones;
    setIncludeBones(newIncludeBones);
    track('Bones Inclusion Switch Changed', {
      includeBones: newIncludeBones,
    });
  };

  // Track Instructions Collapse
  const handleInstructionsToggle = () => {
    const newState = !instructionsOpen;
    setInstructionsOpen(newState);
    track('Instructions Toggled', {
      instructionsOpen: newState,
    });
  };

  const calculateCookingTime = () => {
    const weightInLbs = unit === 'lbs' ? weight : weight * 2.20462;

    let baseTime;
    if (meatType === 'prime-rib') {
      baseTime = weightInLbs * 15; // 15 minutes per pound for prime rib
    } else if (meatType === 'turkey') {
      baseTime = weightInLbs * 13; // 13 minutes per pound for turkey
    } else if (meatType === 'chicken') {
      baseTime = weightInLbs * 20; // 20 minutes per pound for chicken
    } else if (meatType === 'lamb') {
      baseTime = weightInLbs * 25; // 25 minutes per pound for lamb
    } else if (meatType === 'pork') {
      baseTime = weightInLbs * 20; // 20 minutes per pound for pork loin/shoulder
    }

    let donenessAdjustment = 0;
    if (meatType === 'prime-rib' || meatType === 'lamb' || meatType === 'pork') {
      switch (doneness) {
        case 'rare':
          donenessAdjustment = -10;
          break;
        case 'medium':
          donenessAdjustment = 10;
          break;
        case 'well-done':
          donenessAdjustment = 20;
          break;
        default:
          donenessAdjustment = 0;
      }
    }

    let bonesAdjustment = 0;
    if (includeBones && meatType === 'prime-rib') {
      bonesAdjustment = numberOfBones * 5;
    }

    return Math.round(baseTime + donenessAdjustment + bonesAdjustment);
  };

  const getFinalTemperatureMin = () => {
    const temperatureRange =
      meatType === 'prime-rib'
        ? {
            rare: { min: 110, max: 115 },
            'medium-rare': { min: 115, max: 120 },
            medium: { min: 125, max: 130 },
            'well-done': { min: 135, max: 140 }, // 135+ for well-done
          }[doneness]
        : meatType === 'turkey'
        ? {
            min: 165, max: 170,
          }
        : meatType === 'chicken'
        ? {
            min: 165, max: 170,
          }
        : meatType === 'pork'
        ? {
            medium: { min: 145, max: 150 },
            'well-done': { min: 160, max: 165 },
          }[doneness]
        : {
            rare: { min: 125, max: 130 },
            'medium-rare': { min: 130, max: 135 },
            medium: { min: 140, max: 145 },
            'well-done': { min: 150, max: 155 },
          }[doneness];

    if (!temperatureRange) {
      return ''; // Handle undefined temperature range
    }

    if (tempUnit === 'F') {
      return `${temperatureRange.min}°F`;
    } else {
      const minC = Math.round((temperatureRange.min - 32) * (5 / 9));
      return `${minC}°C`;
    }
  };

  const getFinalTemperatureMax = () => {
    const temperatureRange =
      meatType === 'prime-rib'
        ? {
            rare: { min: 110, max: 115 },
            'medium-rare': { min: 115, max: 120 },
            medium: { min: 125, max: 130 },
            'well-done': { min: 135, max: 140 }, // 135+ for well-done
          }[doneness]
        : meatType === 'turkey'
        ? {
            min: 165, max: 170,
          }
        : meatType === 'chicken'
        ? {
            min: 165, max: 170,
          }
        : meatType === 'pork'
        ? {
            medium: { min: 145, max: 150 },
            'well-done': { min: 160, max: 165 },
          }[doneness]
        : {
            rare: { min: 125, max: 130 },
            'medium-rare': { min: 130, max: 135 },
            medium: { min: 140, max: 145 },
            'well-done': { min: 150, max: 155 },
          }[doneness];

    if (!temperatureRange) {
      return ''; // Handle undefined temperature range
    }

    if (tempUnit === 'F') {
      return `${temperatureRange.max}°F`;
    } else {
      const maxC = Math.round((temperatureRange.max - 32) * (5 / 9));
      return `${maxC}°C`;
    }
  };

  const weightSettings =
    unit === 'lbs'
      ? { min: 2, max: meatType === 'prime-rib' ? 20 : meatType === 'turkey' ? 30 : meatType === 'chicken' ? 10 : meatType === 'pork' ? 15 : 15, step: 0.5 }
      : { min: 1, max: meatType === 'prime-rib' ? 9 : meatType === 'turkey' ? 13 : meatType === 'chicken' ? 5 : meatType === 'pork' ? 7 : 7, step: 0.2 };

  return (
    <Box className="w-full max-w-lg mx-auto" p={{ base: 4, md: 6 }} boxShadow="lg" rounded="lg" bg="white">
      <Heading as="h1" size={{ base: 'lg', md: 'xl' }} mb={6} color="teal.600" textAlign={{ base: 'center', md: 'left' }}>
        {meatType === 'prime-rib'
          ? 'Prime Rib Calculator'
          : meatType === 'turkey'
          ? 'Turkey Calculator'
          : meatType === 'chicken'
          ? 'Chicken Calculator'
          : meatType === 'lamb'
          ? 'Lamb Calculator'
          : 'Pork Calculator'}
      </Heading>

      <Stack spacing={{ base: 4, md: 6 }}>
        {/* Meat Type Selection Tabs */}
        <Tabs isFitted size="md" variant="enclosed" colorScheme="teal"
        onChange={handleMeatTypeChange}
        >
          <TabList>
            <Tab>Prime Rib 🥩</Tab>
            <Tab>Turkey 🦃</Tab>
            <Tab>Chicken 🍗</Tab>
            <Tab>Lamb 🐑</Tab>
            <Tab>Pork 🐖</Tab>
          </TabList>
        </Tabs>

        {/* Weight Slider */}
        <Box>
          <Text fontSize={{ base: 'md', md: 'lg' }} mb={2}>
            Weight ({unit}): {weight}
          </Text>
          <Slider
            value={weight}
            onChange={(val) => {
              setWeight(val);
              handleSliderChange(val);
            }}
            min={weightSettings.min}
            max={weightSettings.max}
            step={weightSettings.step}
          >
            <SliderTrack height="8px" borderRadius="10px" bg="teal.100">
              <SliderFilledTrack bg="teal.500" />
            </SliderTrack>
            <SliderThumb boxSize={6}>
            <Box as={MdGraphicEq} size="60px" />
          </SliderThumb>
          </Slider>
        </Box>

        // Desired Doneness Buttons
{(meatType === 'prime-rib' || meatType === 'lamb' || meatType === 'pork') && (
  <Box>
    <Text fontSize={{ base: 'md', md: 'lg' }} mb={2}>
      Desired Doneness
    </Text>
    <Flex justifyContent="space-between" mt={2} flexWrap="wrap" gap={2}>
      {['rare', 'medium-rare', 'medium', 'well-done'].map((level) => (
        <Button
          key={level}
          onClick={() => handleDonenessClick(level)} // Use the tracking function here
          colorScheme={doneness === level ? 'teal' : 'gray'}
          variant={doneness === level ? 'solid' : 'outline'}
          width={{ base: '100%', md: 'auto' }}
          isDisabled={meatType === 'pork' && (level === 'rare' || level === 'medium-rare')}
        >
          {level.charAt(0).toUpperCase() + level.slice(1).replace('-', ' ')}
        </Button>
      ))}
    </Flex>
  </Box>
)}


        {/* Bones Inclusion Switch for Prime Rib */}
        {meatType === 'prime-rib' && (
          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="bones-switch" mb="0" mr={4}>
              <Text fontSize={{ base: 'md', md: 'lg' }}>Include Bones</Text>
            </FormLabel>
            <Switch
              id="bones-switch"
              isChecked={includeBones}
              onChange={() => setIncludeBones(!includeBones)}
              colorScheme="teal"
            />
          </FormControl>
        )}

{includeBones && meatType === 'prime-rib' && (
  <Box>
    <Text fontSize={{ base: 'md', md: 'lg' }} mb={2}>
      Number of Bones
    </Text>
    <Flex justifyContent="space-between" mt={2} flexWrap="wrap" gap={2}>
      {[1, 2, 3, 4, 5].map((bone) => (
        <Button
          key={bone}
          onClick={() => setNumberOfBones(bone)}
          colorScheme={numberOfBones === bone ? 'teal' : 'gray'}
          variant={numberOfBones === bone ? 'solid' : 'outline'}
          width={{ base: '100%', md: 'auto' }}
        >
          {'🦴'.repeat(bone)}
        </Button>
      ))}
    </Flex>
  </Box>
)}


        {/* Cooking Time and Temperature Display */}
        <Box mt={6} p={4} bg="gray.50" rounded="lg" shadow="md" border="1px" borderColor="teal.200">
        <Flex alignItems="center" mb={4} onClick={() => setInstructionsOpen(!instructionsOpen)} cursor="pointer">
  <Heading as="h3" size="lg" color="teal.600">
    Oven Cooking Instructions
  </Heading>
  <IconButton
    aria-label="Toggle Instructions"
    icon={instructionsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
    variant="ghost"
    ml={2}
  />
</Flex>


          <Flex alignItems="center">
            <Text fontSize="lg">
              Cooking Time: {calculateCookingTime()} minutes{' '}
              <Tooltip label="Most importantly, it's the internal temperature rather than the time that determines doneness." aria-label="Cooking Time Tooltip">
                <Text as="span" fontSize="sm" color="gray.600" cursor="pointer" _hover={{ textDecoration: 'underline' }}>
                  (Approximate)
                </Text>
              </Tooltip>
            </Text>
          </Flex>

          <Text fontSize="xl" mt={4}>
  {meatType === 'prime-rib' ? (
    <>
      Take the meat out once it reaches <Text as="span" color="teal.500" fontWeight="bold">{getFinalTemperatureMin()}</Text> and let it rest until it reaches <Text as="span" color="teal.500" fontWeight="bold">{getFinalTemperatureMax()}</Text>.
    </>
  ) : meatType === 'turkey' ? (
    <>
      Take the turkey out once the <Text as="span" color="teal.500" fontWeight="bold">breast</Text> reaches <Text as="span" color="teal.500" fontWeight="bold">{getFinalTemperatureMin()}</Text> or the thigh reaches <Text as="span" color="teal.500" fontWeight="bold">{getFinalTemperatureMax()}</Text>.
    </>
  ) : meatType === 'chicken' ? (
    <>
      Take the chicken out once the <Text as="span" color="teal.500" fontWeight="bold">breast</Text> reaches <Text as="span" color="teal.500" fontWeight="bold">{getFinalTemperatureMin()}</Text>.
    </>
  ) : meatType === 'lamb' ? (
    <>
      Take the lamb out once it reaches <Text as="span" color="teal.500" fontWeight="bold">{getFinalTemperatureMin()}</Text> and let it rest until it reaches <Text as="span" color="teal.500" fontWeight="bold">{getFinalTemperatureMax()}</Text>.
    </>
  ) : (
    <>
      Take the pork out once it reaches <Text as="span" color="teal.500" fontWeight="bold">{getFinalTemperatureMin()}</Text> and let it rest until it reaches <Text as="span" color="teal.500" fontWeight="bold">{getFinalTemperatureMax()}</Text>.
    </>
  )}
</Text>



          <Collapse in={instructionsOpen} animateOpacity>
          <Divider my={4} />
            <Text fontSize="md" mb={4} color="black">
              Cooking a {meatType === 'prime-rib' ? 'prime rib roast' : meatType === 'turkey' ? 'turkey' : meatType === 'chicken' ? 'chicken' : meatType === 'lamb' ? 'lamb' : 'pork'} requires careful attention to both time and temperature. Follow these general guidelines for the best results:
            </Text>
            <Text fontSize="md" mb={4} color="black">
              1. Preheat your oven to the desired temperature based on your cooking preference (e.g., {tempUnit === 'F' ? '450°F' : '220°C'} for searing, then reduce to {tempUnit === 'F' ? '325°F' : '160°C'}).
            </Text>
            <Text fontSize="md" mb={4} color="black">
              2. Season your {meatType === 'prime-rib' ? 'prime rib roast' : meatType === 'turkey' ? 'turkey' : meatType === 'chicken' ? 'chicken' : meatType === 'lamb' ? 'lamb' : 'pork'} generously with salt, pepper, and other preferred herbs and spices.
            </Text>
            <Text fontSize="md" mb={4} color="black">
              3. Place the {meatType === 'prime-rib' ? 'roast' : meatType === 'turkey' ? 'turkey' : meatType === 'chicken' ? 'chicken' : meatType === 'lamb' ? 'lamb' : 'pork'} on a rack in a roasting pan, bone-side down if applicable.
            </Text>
            <Text fontSize="md" mb={4} color="black">
              4. Roast until the internal temperature reaches the desired doneness level. Use a meat thermometer to check the temperature accurately.
            </Text>
            {meatType === 'turkey' && (
              <Text fontSize="md" mb={4} color="black">
                Note: For turkey, ensure the breast reaches at least {getFinalTemperatureMin()} and the thigh reaches at least {getFinalTemperatureMax()}.
              </Text>
            )}
            {meatType === 'chicken' && (
              <Text fontSize="md" mb={4} color="black">
                Note: For chicken, ensure the internal temperature reaches at least {getFinalTemperatureMin()} for safe consumption.
              </Text>
            )}
            {meatType === 'lamb' && (
              <Text fontSize="md" mb={4} color="black">
                Note: For lamb, cook to the desired doneness, ensuring the internal temperature reaches between {getFinalTemperatureMin()} and {getFinalTemperatureMax()}.
              </Text>
            )}
            {meatType === 'pork' && (
              <Text fontSize="md" mb={4} color="black">
                Note: For pork loin or shoulder, cook until the internal temperature reaches between {getFinalTemperatureMin()} and {getFinalTemperatureMax()}, depending on your preferred doneness.
              </Text>
            )}
          </Collapse>

      

         


        </Box>

        {/* Centered Share Icons */}
        <Flex justifyContent="center" gap={4} wrap="wrap" mt={4}>
        <WhatsappShareButton url={shareUrl} title={title}>
            <WhatsappIcon size={32} round />
          </WhatsappShareButton>

          <LinkedinShareButton url={shareUrl} title={title}>
            <LinkedinIcon size={32} round />
          </LinkedinShareButton>

          <FacebookShareButton url={shareUrl} quote={title} hashtag="#cooking">
            <FacebookIcon size={32} round />
          </FacebookShareButton>

          <TwitterShareButton url={shareUrl} title={title} hashtags={['Cooking', 'Recipes']}>
            <TwitterIcon size={32} round />
          </TwitterShareButton>

          <RedditShareButton url={shareUrl} title={title}>
            <RedditIcon size={32} round />
          </RedditShareButton>

          <PinterestShareButton url={shareUrl} media="https://cooktimecalc.com/path_to_image.jpg" description={title}>
            <PinterestIcon size={32} round />
          </PinterestShareButton>

          <TelegramShareButton url={shareUrl} title={title}>
            <TelegramIcon size={32} round />
          </TelegramShareButton>

          <EmailShareButton url={shareUrl} subject={title} body="Check out this amazing cooking calculator:">
            <EmailIcon size={32} round />
          </EmailShareButton>

          <TumblrShareButton url={shareUrl} title={title} tags={['cooking', 'recipes']}>
            <TumblrIcon size={32} round />
          </TumblrShareButton>
        </Flex>
      </Stack>

                    {/* AdSense Ad - took it out*/}
                    <Box textAlign="center" mb={{ base: 4, md: 6 }}>
        </Box>
        

                {/* Unit and Temperature Switches */}
                <Flex alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <FormControl display="flex" alignItems="center" mb={{ base: 4, md: 0 }}>
            <FormLabel htmlFor="unit-switch" mb="0" mr={4}>
              <Text fontSize={{ base: 'md', md: 'lg' }}>Temperature: lbs/kg</Text>
            </FormLabel>
            <Switch
              id="unit-switch"
              isChecked={unit === 'kgs'}
              onChange={() => setUnit(unit === 'lbs' ? 'kgs' : 'lbs')}
              colorScheme="teal"
            />
          </FormControl>

          <FormControl display="flex" alignItems="center" mb={{ base: 4, md: 0 }}>
            <FormLabel htmlFor="temp-unit-switch" mb="0" mr={4}>
              <Text fontSize={{ base: 'md', md: 'lg' }}>Weight: °F/°C</Text>
            </FormLabel>
            <Switch
              id="temp-unit-switch"
              isChecked={tempUnit === 'C'}
              onChange={() => setTempUnit(tempUnit === 'F' ? 'C' : 'F')}
              colorScheme="teal"
            />
          </FormControl>
        </Flex>
    </Box>
  );
}